import { ElementMeasurement } from '../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';

type LoggerType = {
    info: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
};

const DIAGNOSTIC_COLUMN_JITTER_LOG_INTERVAL = 25;
const DIAGNOSTIC_CONTAIN_INTRINSIC_LOG_INTERVAL = 30;
const DIAGNOSTIC_FORCE_REMEASURE_LOG_INTERVAL = 20;
const DIAGNOSTIC_COLLAPSE_SKIP_LOG_INTERVAL = 10;
const DIAGNOSTIC_PROP_UPDATE_LOG_INTERVAL = 25;
const DIAGNOSTIC_MEASURE_BURST_WINDOW_MS = 120;
const DIAGNOSTIC_MEASURE_BURST_LOG_INTERVAL = 30;
const DIAGNOSTIC_POSITION_JITTER_LOG_INTERVAL = 20;
const DIAGNOSTIC_OBSERVER_SETUP_LOG_INTERVAL = 10;
const DIAGNOSTIC_FAST_VISIBILITY_FLAP_MS = 250;
const DIAGNOSTIC_FAST_VISIBILITY_FLAP_LOG_INTERVAL = 10;
const DIAGNOSTIC_ZOOM_DEFERRED_LOG_INTERVAL = 10;
const DIAGNOSTIC_FIRST_VISIBILITY_CALLBACK_LATENCY_MS = 200;
const DIAGNOSTIC_POSITION_JITTER_MAX_DELTA_PX = 1;
const DIAGNOSTIC_STABLE_SIZE_MAX_DELTA_PX = 0.5;

const initialCounts = {
    hasLoggedSession: false,
    collapseSkipCount: 0,
    smallJitterCount: 0,
    containIntrinsicWriteCount: 0,
    forceRemeasureCount: 0,
    previousColumnHeightDirection: 0,
    jitterDirectionFlipCount: 0,
    externalElementUpdateCount: 0,
    measureBurstCount: 0,
    lastMeasureTimestamp: 0,
    lastExternalElementUpdateTimestamp: 0,
    positionJitterCount: 0,
    observerSetupCount: 0,
    zoomDeferredCount: 0,
    visibilityFlapCount: 0,
    lastVisibilityValue: null as null | boolean,
    lastVisibilityChangeTimestamp: 0,
    observerSetupTimestamp: 0,
    firstIntersectionCallbackLogged: false,
};

class SafariScrollDiagnostics {
    logger: LoggerType;
    counts = { ...initialCounts };

    constructor(logger: LoggerType) {
        this.logger = logger;
    }

    recordSessionStart = (id: string, elementType: string) => {
        if (this.counts.hasLoggedSession) return;

        this.counts.hasLoggedSession = true;
        this.logger.info('Safari thrash diagnostics enabled', {
            id,
            elementType,
        });
    };

    recordExternalElementUpdate = (id: string) => {
        const now = Date.now();
        const msSincePreviousUpdate = this.counts.lastExternalElementUpdateTimestamp
            ? now - this.counts.lastExternalElementUpdateTimestamp
            : null;
        this.counts.lastExternalElementUpdateTimestamp = now;
        this.counts.externalElementUpdateCount += 1;

        if (this.counts.externalElementUpdateCount % DIAGNOSTIC_PROP_UPDATE_LOG_INTERVAL !== 0) return;

        this.logger.warn('High frequency element prop updates without location change', {
            id,
            externalElementUpdateCount: this.counts.externalElementUpdateCount,
            msSincePreviousUpdate,
        });
    };

    recordMeasureStart = (id: string, syncedMeasurementCount: number) => {
        const now = Date.now();
        const msSinceLastMeasure = this.counts.lastMeasureTimestamp ? now - this.counts.lastMeasureTimestamp : null;
        this.counts.lastMeasureTimestamp = now;

        if (msSinceLastMeasure === null || msSinceLastMeasure > DIAGNOSTIC_MEASURE_BURST_WINDOW_MS) return;

        this.counts.measureBurstCount += 1;
        if (this.counts.measureBurstCount % DIAGNOSTIC_MEASURE_BURST_LOG_INTERVAL !== 0) return;

        this.logger.warn('Rapid measure callback bursts detected', {
            id,
            measureBurstCount: this.counts.measureBurstCount,
            msSinceLastMeasure,
            syncedMeasurementCount,
        });
    };

    recordCollapseSkip = (id: string, height: number, previousHeight: number) => {
        this.counts.collapseSkipCount += 1;
        if (this.counts.collapseSkipCount % DIAGNOSTIC_COLLAPSE_SKIP_LOG_INTERVAL !== 0) return;

        this.logger.warn('Skipped potential Safari off-screen collapse measurement', {
            id,
            collapseSkipCount: this.counts.collapseSkipCount,
            height,
            previousHeight,
        });
    };

    recordPositionOnlyJitter = (
        id: string,
        isContentVisible: boolean,
        previousMeasurements?: ElementMeasurement,
        measurements?: ElementMeasurement,
    ) => {
        const previousBottom = previousMeasurements?.bottom;
        const currentBottom = measurements?.bottom;
        const previousOriginOffsetY = previousMeasurements?.originOffset?.y;
        const currentOriginOffsetY = measurements?.originOffset?.y;

        const bottomDelta = Math.abs((currentBottom || 0) - (previousBottom || 0));
        const originOffsetYDelta = Math.abs((currentOriginOffsetY || 0) - (previousOriginOffsetY || 0));
        const widthDelta = Math.abs((measurements?.width || 0) - (previousMeasurements?.width || 0));
        const heightDelta = Math.abs((measurements?.height || 0) - (previousMeasurements?.height || 0));

        const looksLikePositionOnlyJitter =
            bottomDelta > 0 &&
            bottomDelta <= DIAGNOSTIC_POSITION_JITTER_MAX_DELTA_PX &&
            originOffsetYDelta > 0 &&
            originOffsetYDelta <= DIAGNOSTIC_POSITION_JITTER_MAX_DELTA_PX &&
            widthDelta <= DIAGNOSTIC_STABLE_SIZE_MAX_DELTA_PX &&
            heightDelta <= DIAGNOSTIC_STABLE_SIZE_MAX_DELTA_PX;

        if (looksLikePositionOnlyJitter) {
            this.counts.positionJitterCount += 1;
            if (this.counts.positionJitterCount % DIAGNOSTIC_POSITION_JITTER_LOG_INTERVAL !== 0) return;

            this.logger.warn('Detected position-only measurement jitter (likely visibility boundary rounding)', {
                id,
                positionJitterCount: this.counts.positionJitterCount,
                bottomDelta,
                originOffsetYDelta,
                isContentVisible,
            });
        }
    };

    recordColumnHeightJitter = (id: string, heightChange: number) => {
        this.counts.smallJitterCount += 1;
        const heightDirection = Math.sign(heightChange);

        if (
            this.counts.previousColumnHeightDirection &&
            heightDirection &&
            this.counts.previousColumnHeightDirection !== heightDirection
        ) {
            this.counts.jitterDirectionFlipCount += 1;
        }
        this.counts.previousColumnHeightDirection = heightDirection;

        if (this.counts.smallJitterCount % DIAGNOSTIC_COLUMN_JITTER_LOG_INTERVAL !== 0) return;

        this.logger.warn('Column height jitter detected while Safari diagnostics are enabled', {
            id,
            smallJitterCount: this.counts.smallJitterCount,
            jitterDirectionFlipCount: this.counts.jitterDirectionFlipCount,
            heightChange,
        });
    };

    recordForceRemeasure = (id: string, childCount: number, heightChange: number) => {
        this.counts.forceRemeasureCount += 1;
        if (this.counts.forceRemeasureCount % DIAGNOSTIC_FORCE_REMEASURE_LOG_INTERVAL !== 0) return;

        this.logger.warn('Column force remeasure triggered for children', {
            id,
            forceRemeasureCount: this.counts.forceRemeasureCount,
            childCount,
            heightChange,
        });
    };

    recordContainIntrinsicUpdate = (id: string, heightChange: number) => {
        this.counts.containIntrinsicWriteCount += 1;
        if (this.counts.containIntrinsicWriteCount % DIAGNOSTIC_CONTAIN_INTRINSIC_LOG_INTERVAL !== 0) return;

        this.logger.warn('contain-intrinsic-size has been updated repeatedly', {
            id,
            containIntrinsicWriteCount: this.counts.containIntrinsicWriteCount,
            heightChange,
        });
    };

    recordObserverSetup = (elementId: string, rootFound: boolean) => {
        this.counts.observerSetupCount += 1;
        this.counts.observerSetupTimestamp = Date.now();
        this.counts.firstIntersectionCallbackLogged = false;

        if (this.counts.observerSetupCount % DIAGNOSTIC_OBSERVER_SETUP_LOG_INTERVAL === 0) {
            this.logger.info('Recreating content visibility observer', {
                elementId,
                observerSetupCount: this.counts.observerSetupCount,
                rootFound,
            });
        }

        if (rootFound) return;

        this.logger.warn('Content visibility observer root not found; browser viewport will be used', {
            elementId,
        });
    };

    recordFirstIntersectionCallback = (elementId: string, isIntersecting: boolean) => {
        if (this.counts.firstIntersectionCallbackLogged) return;

        this.counts.firstIntersectionCallbackLogged = true;
        const callbackLatencyMs = Date.now() - this.counts.observerSetupTimestamp;

        this.logger.info('First intersection callback after observer setup', {
            elementId,
            callbackLatencyMs,
            delayed: callbackLatencyMs > DIAGNOSTIC_FIRST_VISIBILITY_CALLBACK_LATENCY_MS,
            isIntersecting,
        });
    };

    recordVisibilityChange = (elementId: string, isVisible: boolean) => {
        const now = Date.now();

        if (this.counts.lastVisibilityValue !== null) {
            const hasVisibilityChanged = this.counts.lastVisibilityValue !== isVisible;
            const changeDeltaMs = now - this.counts.lastVisibilityChangeTimestamp;

            if (hasVisibilityChanged && changeDeltaMs <= DIAGNOSTIC_FAST_VISIBILITY_FLAP_MS) {
                this.counts.visibilityFlapCount += 1;

                if (this.counts.visibilityFlapCount % DIAGNOSTIC_FAST_VISIBILITY_FLAP_LOG_INTERVAL === 0) {
                    this.logger.warn('Detected rapid visibility flap in observer', {
                        elementId,
                        visibilityFlapCount: this.counts.visibilityFlapCount,
                        changeDeltaMs,
                    });
                }
            }
        }

        this.counts.lastVisibilityValue = isVisible;
        this.counts.lastVisibilityChangeTimestamp = now;
    };

    recordZoomDeferred = (elementId: string) => {
        this.counts.zoomDeferredCount += 1;
        if (this.counts.zoomDeferredCount % DIAGNOSTIC_ZOOM_DEFERRED_LOG_INTERVAL !== 0) return;

        this.logger.warn('Visibility update deferred while zooming', {
            elementId,
            zoomDeferredCount: this.counts.zoomDeferredCount,
        });
    };
}

export default SafariScrollDiagnostics;
